import styled from '@emotion/styled'
import Icon from 'components/patterns/Icon'
import { Paragraph } from 'components/patterns/Typography'
import { themeColor } from 'utils/styles'
import { ssrWarning } from 'utils/emotion'

const withStyle = (Alert) =>
  styled(Alert)((props) => {
    const { alertButtonHoverBgColor, alertButtonHoverColor } = props.theme
    const type = getStylesByAlertType(props.type, props)
    const color = getAlertTextColor(props.type, props)
    return `
        font-size: 16px;
        font-weight: normal;
        font-style: normal;
        font-stretch: normal;
        line-height: 1.38;
        letter-spacing: normal;
        width: 100%;
        margin-bottom: 10px;
        font-family: ${props.theme.fontFamily};
        padding: 9px ${props.closable ? `40px` : `20px`} 9px 46px;
        &&.ant-alert-banner {
          height: 45px;
          ${type}
        }
        .ant-alert-message {
          display: none;
        }
        .ant-alert-description {
          margin-top: 2px;
          display: flex;
          flex-direction: row;
          align-items: baseline;
        }
        #root & {
          border-top: 1px solid ${themeColor('grey', props)};
        }
        && a.ant-alert-close-icon {
          display: inline-block;
          pointer-events: ${!props.closable ? 'none' : 'auto'}
        }
        && span.ant-alert-close-icon {
          top: 10px;
        }
        && i.anticon-close {
          font-size: 10px;
          vertical-align: middle;
          &:hover {
            color: ${color};
            opacity: 0.5;
          }
        }
        && button {
          margin: 0;
          margin-left: 10px;
          top: -2px;
          &:hover {
            background-color: ${alertButtonHoverBgColor};
            color: ${alertButtonHoverColor};
          }
          a, a:hover, a:active {
            text-decoration: none;
          }
        }
      `
  })

export default withStyle

const getAlertTextColor = (type, props) => {
  const alertTypes = {
    success: `${themeColor('whitePrimary', props)}`,
    info: `${themeColor('whitePrimary', props)}`,
    warning: `${themeColor('blackSecondary', props)}`,
    error: `${themeColor('whitePrimary', props)}`,
  }
  return alertTypes[type]
}

export const StyledAlertIconContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  &&&&.ant-alert-icon {
    top: -2.5px;
    left: -4px;
    height: 48px;
  }
`

export const AlertIcon = styled(Icon)`
  &&& {
    height: 18px;
    width: 24px;
    left: 12px;
  }
`

export const StyledIcon = styled(Icon)((props) => {
  const { hasDecoration } = props
  const iconStylesWithDeco = `
    &&& {
      height: 18px;
      width: 24px;
      margin-left: 6px;
    }
  `
  const iconStyles = `
    &&& {
      height: 17px;
      width: 27px;
      margin-top: 0px;
      margin-left: 12px;
    }
   `
  return `
  &.chec-icon--warning_solid_black path:nth-of-type(2) ${ssrWarning} {
    fill: ${themeColor('black', props)};
  }
   ${hasDecoration ? iconStylesWithDeco : iconStyles};
  `
})

export const AlertGridIcon = styled(Icon)`
  &&& {
    height: 44px;
  }
`

const getStylesByAlertType = (type, props) => {
  const alertTypes = {
    success: `
      background-color: ${themeColor('confirmSecondary', props)};
      color: ${themeColor('whitePrimary', props)};
      border: 1px solid ${themeColor('confirmSecondary', props)};
      
      && i {
        color: ${themeColor('whitePrimary', props)};
      }
      && button {
        color: $${themeColor('whitePrimary', props)};
        border-color: ${themeColor('whitePrimary', props)};
      }
      && a {
        color: ${themeColor('whitePrimary', props)};
        text-decoration: underline;
      }
      .ant-alert-description {
        margin-left: -2px;
      }
    `,
    info: `
      color: ${themeColor('blackSecondary', props)};
      background-color: ${themeColor('whitePrimary', props)};
      border: 1px solid ${themeColor('blueSecondary', props)} !important;
      && i {
        color: ${themeColor('blackSecondary', props)};
      }
      && button {
        color: ${themeColor('blueSecondary', props)};
        border-color: ${themeColor('blueSecondary', props)};
      }
      && a {
        color: ${themeColor('linkColor', props)};
        text-decoration: underline;
      }
      .ant-alert-description {
        margin-left: 4px;
      }
    `,
    warning: `
      color: ${themeColor('blackSecondary', props)};
      background-color: ${themeColor('whitePrimary', props)};
      border: 1px solid ${themeColor('warningPrimary', props)} !important;
      && i {
        color: ${themeColor('blackSecondary', props)};
      }
      && button {
        color: ${themeColor('bluePrimary', props)};
        border-color: ${themeColor('bluePrimary', props)}
      }
      && a {
        color: ${themeColor('blackSecondary', props)}
        text-decoration: underline;
      }
      .ant-alert-description {
        margin-left: 4px;
      }
    `,
    error: `
      background-color: ${themeColor('errorPrimary', props)};
      color: ${themeColor('whitePrimary', props)};
      border-color: ${themeColor('errorPrimary', props)};
      && i {
        color: ${themeColor('whitePrimary', props)};
      }
      && button {
        color: ${themeColor('whitePrimary', props)};
        border-color: ${themeColor('whitePrimary', props)};
      }
      && a {
        color: ${themeColor('whitePrimary', props)};
        text-decoration: underline;
      }
      .ant-alert-description {
        margin-left: -2px;
      }
    `,
  }

  return alertTypes[type]
}

const alertMessagePrefixColorByType = {
  success: 'whitePrimary',
  info: 'blackSecondary',
  warning: 'blackSecondary',
  error: 'whitePrimary',
}

export const MessagePrefix = styled(Paragraph)((props) => {
  const { type } = props
  return `
    margin-top: 2px;
    margin-right: 3px;
    font-weight: bold;
    color: ${themeColor(alertMessagePrefixColorByType[type], props)};
  `
})

export const MessageContainer = styled.div`
  display: flex;
  flex-direction: row;
`
export const LinkBox = styled.div((props) => {
  const {
    alertButtonHoverBgColor,
    alertInfoBgColor,
    btnOnColorLightFocusColor,
    btnOnColorLightFocusBorderColor,
  } = props.theme
  return `
    display: inline;
    margin: 0;
    margin-left: 10px;
    border: 1px solid ${alertButtonHoverBgColor};
    border-radius: 3px;
    background-color: ${alertInfoBgColor};
    color: white !important;
    top: -2px;
    padding: 2px 8px;
    &:hover {
      cursor: pointer;
    }
    &:focus {
      color: ${btnOnColorLightFocusColor};
      border-color: ${btnOnColorLightFocusBorderColor};
    }
    a {
      color: ${alertButtonHoverBgColor};
      font-size: 12px;
      line-height: 1.33;
      letter-spacing: 0.3px;
    }
    a:hover, a:active {
      text-decoration: none;
    }
    `
})
